
import Vue from "vue";
import { SyncManagers } from "../Sync";
import { Transaction } from "../Ledger";
import { UserID, Versioned } from "../Utils";
import { round } from "../math";
import {s} from "../L10n";
import {prettyCcy} from "../Currency";

function getBalances(
  items: Transaction[],
  participants: Map<string, Versioned<string>>
): Map<string, Map<string, number>> {
  // First calculate balances by user ID
  const balancesByUserId: Map<string, Map<string, number>> = new Map();
  const addToBalance = (uid: string, amt: number, ccy: string) => {
    let userBalances = balancesByUserId.get(uid);
    if (!userBalances) {
      userBalances = new Map();
      balancesByUserId.set(uid, userBalances);
    }
    let bal = userBalances.get(ccy) || 0;
    userBalances.set(ccy, bal + amt);
    return userBalances;
  };
  // const addToBalance = (uid: string, n: number) => {
    // return balancesByUserId.set(uid, (balancesByUserId.get(uid) || 0) + n);
  // };
  for (let item of items) {
    addToBalance(item.paidBy.content, item.amount.content, item.currency.content);
    let perPersonShare = item.amount.content / item.paidFor.content.length;
    for (let uid of item.paidFor.content) {
      addToBalance(uid, -perPersonShare, item.currency.content);
    }
  }

  // Convert user IDs to names in the final balance map
  const balancesByName: Map<string, Map<string, number>> = new Map();
  for (let [uid, balance] of balancesByUserId.entries()) {
    const name = participants.get(uid);
    if (!name || balance == null) {
      continue;
    }
    balancesByName.set(name.content, balance);
  }
  return balancesByName;
}

type Transfer = {
  debtor: string,
  creditor: string,
  amount: string,
  currency: string,
  key: string,
}

function computeTransfersCcys(
  balances: Map<string, Map<string, number>>,
  allCcys: Set<string>,
): Transfer[] | undefined {
  // For each currency, compute transfers
  const transfers: Transfer[] = [];
  for (let ccy of allCcys) {
    // Extract balances for this currency
    const balForCcy: Map<string, number> = new Map();
    for (let [user, balMap] of balances.entries()) {
      const bal = balMap.get(ccy) || 0;
      balForCcy.set(user, bal);
    }
    const transfersForCcy = computeTransfers(balForCcy, ccy);
    if (!transfersForCcy) {
      return;
    }
    transfers.push(...transfersForCcy);
  }
  return transfers;
}

/**
* Calculates the optimal set of reimbursements to settle all balances.
* Complexity: O(n^2) where n is the number of participants.
*/
function computeTransfers(
  balances: Map<string, number>,
  currency: string,
): Transfer[] | undefined {
  const transfers = [];
  const ROUNDING_TOLERANCE = 0.01;
  while (true) {
    // In each iteration, the balance of either
    // maxCreditor or maxDebtor will be settled
    let maxCredit = 0, // positive
        maxDebt = 0, // negative
        maxCreditor = null,
        maxDebtor = null;
    for (let [user, balance] of balances.entries()) {
      if (balance > maxCredit) {
        maxCredit = balance;
        maxCreditor = user;
      }
      if (balance < maxDebt) {
        maxDebt = balance;
        maxDebtor = user;
      }
    }
    if (maxCredit < 0 || maxDebt > 0) {
      console.log(
        "Balance.computeTransfers: unexpected signs",
        maxCredit, maxCreditor, maxDebt, maxDebtor
      );
      return;
    }
    if (!maxCreditor || !maxDebtor) break; // all zeroes
    if (Math.abs(maxCredit) <= ROUNDING_TOLERANCE || Math.abs(maxDebt) <= ROUNDING_TOLERANCE)
      break;
    balances.set(maxCreditor, Math.max(maxCredit + maxDebt, 0));
    balances.set(maxDebtor, Math.min(maxCredit + maxDebt, 0));
    const amount = round(Math.min(maxCredit, -maxDebt));
    transfers.push({
      debtor: maxDebtor,
      creditor: maxCreditor,
      amount,
      currency: prettyCcy(currency),
      key: `${maxDebtor}-${maxCreditor}-${amount}`,
    });
  }
  return transfers;
}

export default Vue.extend({
  props: {
    ledgerId: String,
  },
  data() {
    const sync = SyncManagers.get(this.ledgerId);
    return {
      sync: sync,
    };
  },
  methods: {
    back() {
      this.$router.push({ name: "ledger", params: { id: this.sync.id } });
    },
    onExport() {
      const csvColumns: string[] = ["Date", "Description", "Amount", "Currency"];
      const rows: (string | number)[][] = [csvColumns];
      const participants = this.sync.participantList;
      const defaultRow: any[] = ["", "", 0];
      const userIndex: Map<UserID, number> = new Map();
      const set = (row: any[], who: UserID, amt: number) => {
        const i = userIndex.get(who);
        if (!i) throw Error("onExport error");
        row[i] = (row[i] ?? 0) + amt;
      };
      for (let user of participants) {
        userIndex.set(user.value, csvColumns.length);
        csvColumns.push(user.text);
        defaultRow.push(0);
      }

      for (let item of this.sync.items) {
        const row = defaultRow.map(x => x); // copy
        rows.push(row);
        const amount = item.amount.content;
        const paidBy = item.paidBy.content;
        const paidFor = item.paidFor.content;
        row[0] = item.effective_ts.content;
        row[1] = item.description.content;
        row[2] = amount;
        row[3] = item.currency.content;
        set(row, paidBy, amount);
        let perPersonShare = amount / paidFor.length;
        for (let p of paidFor) {
          set(row, p, -perPersonShare);
        }
      }
      const csv = rows.map(row => row.map((x: any) => JSON.stringify(x)).join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const link = document.createElement("a");
      link.download = "transactions.csv";
      link.href = window.URL.createObjectURL(blob);
      link.click();
    },
    s(str: string): string {
      return s(str);
    },
    saveFXs() {
      // TODO
    },
  },
  computed: {
    items(): {name: string, balance: string}[] {
      return Array.from(this.balances.entries()).map(([name, amounts]) =>
        Array.from(amounts.entries()).map(([ccy, amount]) => ({
          name: name,
          balance: round(-amount) + " " + prettyCcy(ccy),
        }))).flat();
    },
    balances(): Map<string, Map<string, number>> {
      return getBalances(
        this.sync.items,
        this.sync.participants
      );
    },
    reimbursements(): Transfer[] | undefined {
      // important: make a copy of the map (it will be mutated by computeTransfersCcys)
      const balancesDeepCopy = structuredClone(this.balances);
      return computeTransfersCcys(balancesDeepCopy, this.ccys);
    },
    name() {
      return this.sync.name;
    },
    // FX
    ccys(): Set<string> {
      const ccys_ = new Set<string>();
      for (let balMap of this.balances.values()) {
        for (let ccy of balMap.keys()) {
          ccys_.add(ccy);
        }
      }
      return ccys_;
    },
    isCompo(): boolean {
      // FIXME: TODO
      return false;
      // return this.ccys.size > 1;
    },
  },
});
